import { constants, routes } from "../../constants";
import { SubscriptionFeature } from "../../types/billing/SubscriptionFeature";
import { SecurityBwicStatistics } from "../../types/security/SecurityBwicStatistics";
import { dateTimeUtils, formatUtils, numericUtils } from "../../utils";
import { ActionBlocker } from "../access/ActionBlocker";
import { FeatureLink } from "../access/FeatureLink";
import { Table } from "../bidding/common/table";
import { IColumnDefinition } from "../bidding/common/table/types/ColumnDefinition";
import { Pluralize } from "../common";
import { ColumnBuilder } from "../bidding/common/table/columns/column-builder/ColumnBuilder";
import { user } from "../../user";
import { ValueBlocker } from "../access/ValueBlocker";
import { RequiredFeature } from "../access/RequiredFeature";
import { SubscribeLink } from "../access/BlockedFeatureText";
import { DmAssumptionsTooltip } from "./DmAssumptionsTooltip";
import { PxTalkDmRangeText } from "../common/PxTalk/PxTalkDmRangeText";
import { Wal } from "./Wal";

interface Props {
    ticker: string;
    history: SecurityBwicStatistics[];
    feature?: SubscriptionFeature;
}

export function DmWalTradingHistory({ history, ticker, feature }: Props) {
    const items = !feature || user.hasFeatures(feature)
        ? history
        : history.length ? [history[0]] : [];

    const columns: IColumnDefinition<SecurityBwicStatistics>[] = [{
        columnKey: 'date',
        renderColumnHeaderContent: () => 'Date',
        renderColumnContent: bwic => {
            const dateFilter = dateTimeUtils.utcToLocalString(bwic.bidsDueUtc, 'YYYY-M-D');

            return (
                <ActionBlocker feature={SubscriptionFeature.BwicMonitorSecuritySearch}>
                    <FeatureLink
                        className="btn-link secondary text-regular"
                        path={`${routes.allBWICs}?isinCusipsAndTickers=${ticker}&dateFrom=${dateFilter}&dateTo=${dateFilter}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {dateTimeUtils.utcToLocalString(bwic.bidsDueUtc, 'MM/DD/YY')}
                    </FeatureLink>
                </ActionBlocker>
            );
        },
        className: 'data-list-cell-xs-sm',
    }, {
        columnKey: 'price',
        renderColumnHeaderContent: () => 'Price',
        renderColumnContent: bwic => numericUtils.isNumber(bwic.evalNColor)
            ? formatUtils.formatDecimal(bwic.evalNColor) : constants.emptyPlaceholder,
        className: 'data-list-cell-xs text-right',
    }, {
        columnKey: 'dm',
        renderColumnHeaderContent: () => <>DM to <br /> Mty</>,
        renderColumnContent: bwic =>
            <ValueBlocker feature={feature}>
                {formatUtils.formatDiscountMargin(bwic.colorDm) ?? constants.emptyPlaceholder}
            </ValueBlocker>,
        className: 'data-list-cell-xs text-right',
    }, {
        columnKey: 'px-talk-dm',
        renderColumnHeaderContent: () => <>Px Talk <br /> DM <DmAssumptionsTooltip /></>,
        renderColumnContent: bwic =>
            <ValueBlocker feature={feature}>
                <PxTalkDmRangeText priceTalks={bwic.pxTalks} />
            </ValueBlocker>,
        className: 'data-list-cell-px-talk-dm text-right',
    }, {
        columnKey: 'k-talk-dm',
        renderColumnHeaderContent: () => <>K-Talk <br /> DM <DmAssumptionsTooltip /></>,
        renderColumnContent: bwic =>
            <ValueBlocker feature={feature}>
                {formatUtils.formatDiscountMargin(bwic.evalPriceTalkDm) ?? constants.emptyPlaceholder}
            </ValueBlocker>,
        className: 'data-list-cell-xs text-right',
    }, {
        columnKey: 'wal',
        renderColumnHeaderContent: () => 'WAL',
        renderColumnContent: bwic =>
            <ValueBlocker feature={feature}>
                <Wal
                    colorWal={bwic.colorWal}
                    evalPriceTalkWal={bwic.evalPriceTalkWal}
                    priceTalks={bwic.pxTalks}
                />
            </ValueBlocker>,
        className: 'data-list-cell-xxs-02 text-right',
    }];

    return (
        <div className="security-information-history">
            <div className="flex-row">
                <h4>Trading History</h4>
                <div className="flex-item-right text-sm text-bold">
                    <Pluralize count={history.length} singular="BWIC" />
                </div>
            </div>

            <div className="security-information-table">
                <Table
                    dataItems={items}
                    columns={columns.map(c => new ColumnBuilder(c))}
                    className="data-list-striped data-list-header-two-rows"
                />
                <RequiredFeature
                    feature={feature}
                    inline
                    text={<><SubscribeLink /> to see more security history records.</>}
                >
                    {null}
                </RequiredFeature>

            </div>
        </div>
    );
}